import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  getBetaCycle,
  upsertBetaCycle,
  upsertBetaWave,
  approveBetaWave,
  upsertBetaDailyLog,
  getBetaActivationBreakdown,
  exportBetaSnapshot,
} from "@/lib/beta.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { toast } from "sonner";
import { ArrowLeft, Check, Download } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/admin/beta/ciclos/$cycleId",
)({
  head: () => ({
    meta: [
      { title: "Ciclo de Beta" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CycleDetailPage,
});

const CYCLE_STATUSES = [
  "planejado",
  "em_andamento",
  "pausado",
  "encerrado",
  "cancelado",
];

function CycleDetailPage() {
  const { cycleId } = Route.useParams();
  const qc = useQueryClient();

  const getFn = useServerFn(getBetaCycle);
  const cycleUpsert = useServerFn(upsertBetaCycle);
  const waveUpsert = useServerFn(upsertBetaWave);
  const waveApprove = useServerFn(approveBetaWave);
  const logUpsert = useServerFn(upsertBetaDailyLog);
  const activationFn = useServerFn(getBetaActivationBreakdown);
  const exportFn = useServerFn(exportBetaSnapshot);

  const data = useQuery({
    queryKey: ["beta-cycle", cycleId],
    queryFn: () => getFn({ data: { cycle_id: cycleId } }),
  });
  const activation = useQuery({
    queryKey: ["beta-activation-breakdown"],
    queryFn: () => activationFn(),
  });

  const invalidateCycle = () =>
    qc.invalidateQueries({ queryKey: ["beta-cycle", cycleId] });

  const updateCycle = useMutation({
    mutationFn: (patch: any) => cycleUpsert({ data: { id: cycleId, ...patch } }),
    onSuccess: () => {
      toast.success("Ciclo atualizado.");
      invalidateCycle();
    },
    onError: (e: any) => toast.error(e?.message ?? "Falha ao salvar."),
  });

  const saveWave = useMutation({
    mutationFn: (payload: any) => waveUpsert({ data: payload }),
    onSuccess: () => {
      toast.success("Onda salva.");
      invalidateCycle();
    },
    onError: (e: any) => toast.error(e?.message ?? "Falha ao salvar."),
  });

  const approveWave = useMutation({
    mutationFn: (waveId: string) => waveApprove({ data: { wave_id: waveId } }),
    onSuccess: () => {
      toast.success("Onda aprovada e liberada.");
      invalidateCycle();
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível aprovar."),
  });

  const saveLog = useMutation({
    mutationFn: (payload: any) => logUpsert({ data: payload }),
    onSuccess: () => {
      toast.success("Diário registrado.");
      invalidateCycle();
    },
    onError: (e: any) => toast.error(e?.message ?? "Falha ao registrar."),
  });

  const cycle: any = data.data?.cycle;
  const waves: any[] = data.data?.waves ?? [];
  const logs: any[] = data.data?.logs ?? [];
  const participants: any[] = data.data?.participants ?? [];

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [logDate, setLogDate] = useState(today);
  const existingLog = logs.find((l) => l.log_date === logDate);

  const doExport = async () => {
    const res = await exportFn();
    const rows = res.rows ?? [];
    const headers = Object.keys(rows[0] ?? { user_id: "" });
    const csv = [
      headers.join(","),
      ...rows.map((r: any) =>
        headers
          .map((h) => JSON.stringify(r[h] ?? "").replace(/^"|"$/g, '"'))
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `beta_snapshot_${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!cycle && data.isFetched) {
    return (
      <div className="space-y-4">
        <Link to="/admin/beta/ciclos" className="text-xs text-[var(--gray-ink)]">
          ← Ciclos
        </Link>
        <p className="text-sm">Ciclo não encontrado.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/beta/ciclos"
        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--gray-ink)] hover:text-[var(--navy)]"
      >
        <ArrowLeft size={14} /> Ciclos
      </Link>

      <PageHeader
        title={cycle?.name ?? "Ciclo"}
        description={
          cycle
            ? `${cycle.starts_on} → ${cycle.ends_on ?? "sem previsão"} • ${cycle.max_participants} vagas • ${participants.length} inscritos`
            : "Carregando…"
        }
        actions={
          <button
            onClick={doExport}
            className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-[var(--gray-soft)]/60"
          >
            <Download size={14} /> Exportar CSV
          </button>
        }
      />

      {/* STATUS + AÇÕES */}
      <section className="grid gap-3 rounded-xl border border-black/[0.06] bg-white p-5 md:grid-cols-4">
        <label className="block text-sm">
          <span className="text-xs text-[var(--gray-ink)]">Status</span>
          <select
            defaultValue={cycle?.status ?? "planejado"}
            onChange={(e) => updateCycle.mutate({ ...cycle, status: e.target.value })}
            className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
          >
            {CYCLE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <div className="rounded-md border border-black/5 bg-[var(--gray-soft)]/40 p-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
            Ativados
          </div>
          <div className="text-xl font-semibold text-emerald-700">
            {activation.data?.ativado ?? "—"}
          </div>
        </div>
        <div className="rounded-md border border-black/5 bg-[var(--gray-soft)]/40 p-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
            Parcialmente ativados
          </div>
          <div className="text-xl font-semibold text-amber-700">
            {(activation.data?.parcial_sem_projeto ?? 0) +
              (activation.data?.parcial_sem_tarefa ?? 0)}
          </div>
          <div className="text-[10px] text-[var(--gray-ink)]/70">
            {activation.data?.parcial_sem_projeto ?? 0} sem projeto •{" "}
            {activation.data?.parcial_sem_tarefa ?? 0} sem 1ª tarefa
          </div>
        </div>
        <div className="rounded-md border border-black/5 bg-[var(--gray-soft)]/40 p-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
            Não ativados
          </div>
          <div className="text-xl font-semibold text-[var(--gray-ink)]">
            {activation.data?.nao_ativado ?? "—"}
          </div>
        </div>
      </section>

      {/* ONDAS + CHECKLIST */}
      <section className="rounded-xl border border-black/[0.06] bg-white">
        <header className="flex items-center justify-between border-b border-black/5 px-5 py-3">
          <h2 className="text-sm font-semibold text-[var(--navy)]">
            Ondas & Checklist de entrada
          </h2>
          <button
            onClick={() =>
              saveWave.mutate({
                cycle_id: cycleId,
                wave_number: (waves.at(-1)?.wave_number ?? 0) + 1,
                planned_size: 5,
              })
            }
            className="rounded-md border border-black/10 px-2 py-1 text-xs font-semibold hover:bg-[var(--gray-soft)]/60"
          >
            + Nova onda
          </button>
        </header>
        <div className="divide-y divide-black/5">
          {waves.length === 0 && (
            <div className="px-5 py-6 text-sm text-[var(--gray-ink)]">
              Nenhuma onda planejada. Sugestão: Onda 1 = 5 · Onda 2 = 10 · Onda
              3 = até 30.
            </div>
          )}
          {waves.map((w) => (
            <WaveBlock
              key={w.id}
              wave={w}
              onSave={(payload) => saveWave.mutate({ ...payload, cycle_id: cycleId, id: w.id })}
              onApprove={() => approveWave.mutate(w.id)}
              busy={saveWave.isPending || approveWave.isPending}
            />
          ))}
        </div>
      </section>

      {/* DIÁRIO OPERACIONAL */}
      <section className="rounded-xl border border-black/[0.06] bg-white p-5">
        <header className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--navy)]">
            Diário operacional
          </h2>
          <input
            type="date"
            value={logDate}
            onChange={(e) => setLogDate(e.target.value)}
            className="rounded-md border border-black/10 px-2 py-1 text-xs"
          />
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            saveLog.mutate({
              cycle_id: cycleId,
              log_date: logDate,
              new_participants: Number(fd.get("new_participants") ?? 0),
              incidents: String(fd.get("incidents") ?? ""),
              feedback_summary: String(fd.get("feedback_summary") ?? ""),
              recurring_questions: String(fd.get("recurring_questions") ?? ""),
              corrections: String(fd.get("corrections") ?? ""),
              decision: String(fd.get("decision") ?? ""),
              notes: String(fd.get("notes") ?? ""),
            });
          }}
          key={existingLog?.id ?? logDate}
          className="mt-4 grid gap-3 text-sm md:grid-cols-2"
        >
          <label className="block">
            <span className="text-xs text-[var(--gray-ink)]">Novos participantes</span>
            <input
              type="number"
              min={0}
              name="new_participants"
              defaultValue={existingLog?.new_participants ?? 0}
              className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
            />
          </label>
          <label className="block">
            <span className="text-xs text-[var(--gray-ink)]">Decisão do dia</span>
            <input
              name="decision"
              defaultValue={existingLog?.decision ?? ""}
              className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs text-[var(--gray-ink)]">Incidentes</span>
            <textarea
              name="incidents"
              rows={2}
              defaultValue={existingLog?.incidents ?? ""}
              className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
            />
          </label>
          <label className="block">
            <span className="text-xs text-[var(--gray-ink)]">Feedbacks relevantes</span>
            <textarea
              name="feedback_summary"
              rows={2}
              defaultValue={existingLog?.feedback_summary ?? ""}
              className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
            />
          </label>
          <label className="block">
            <span className="text-xs text-[var(--gray-ink)]">Dúvidas recorrentes</span>
            <textarea
              name="recurring_questions"
              rows={2}
              defaultValue={existingLog?.recurring_questions ?? ""}
              className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs text-[var(--gray-ink)]">Correções realizadas</span>
            <textarea
              name="corrections"
              rows={2}
              defaultValue={existingLog?.corrections ?? ""}
              className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs text-[var(--gray-ink)]">Notas</span>
            <textarea
              name="notes"
              rows={2}
              defaultValue={existingLog?.notes ?? ""}
              className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5"
            />
          </label>
          <div className="md:col-span-2">
            <button
              disabled={saveLog.isPending}
              className="rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              Salvar diário do dia
            </button>
          </div>
        </form>

        {logs.length > 0 && (
          <div className="mt-6">
            <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
              Histórico
            </h3>
            <ul className="mt-2 divide-y divide-black/5 text-xs">
              {logs.map((l) => (
                <li key={l.id} className="flex items-center justify-between py-2">
                  <span className="font-medium">{l.log_date}</span>
                  <span className="text-[var(--gray-ink)]">
                    {l.new_participants ?? 0} novos •{" "}
                    {l.decision ? l.decision : "sem decisão"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

function WaveBlock({
  wave,
  onSave,
  onApprove,
  busy,
}: {
  wave: any;
  onSave: (payload: any) => void;
  onApprove: () => void;
  busy: boolean;
}) {
  const [checklist, setChecklist] = useState<any[]>(wave.checklist ?? []);
  const [plannedSize, setPlannedSize] = useState<number>(wave.planned_size);
  const [notes, setNotes] = useState<string>(wave.notes ?? "");

  const toggle = (idx: number) =>
    setChecklist((cs) =>
      cs.map((c, i) =>
        i === idx
          ? { ...c, checked: !c.checked, checked_at: new Date().toISOString() }
          : c,
      ),
    );
  const doneCount = checklist.filter((c) => c.checked).length;

  return (
    <div className="px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-[var(--navy)] px-2 py-0.5 text-[10px] font-semibold text-white">
            Onda {wave.wave_number}
          </span>
          <span className="text-xs text-[var(--gray-ink)]">
            status: {wave.status}
            {wave.opened_at
              ? ` • liberada em ${new Date(wave.opened_at).toLocaleDateString("pt-BR")}`
              : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs">
            Tamanho{" "}
            <input
              type="number"
              min={1}
              value={plannedSize}
              onChange={(e) => setPlannedSize(Number(e.target.value))}
              className="ml-1 w-16 rounded-md border border-black/10 px-2 py-1"
            />
          </label>
          <button
            onClick={() =>
              onSave({
                wave_number: wave.wave_number,
                planned_size: plannedSize,
                notes,
                checklist,
                status: wave.status,
              })
            }
            className="rounded-md border border-black/10 px-2 py-1 text-xs font-semibold hover:bg-[var(--gray-soft)]/60"
            disabled={busy}
          >
            Salvar
          </button>
          <button
            onClick={onApprove}
            disabled={busy || doneCount < checklist.length || checklist.length === 0}
            className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-40"
          >
            <Check size={12} className="mr-1 inline" />
            Aprovar & liberar
          </button>
        </div>
      </div>

      <ul className="mt-3 grid gap-1 md:grid-cols-2">
        {checklist.map((c: any, i: number) => (
          <li key={c.key} className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={!!c.checked}
              onChange={() => toggle(i)}
            />
            <span className={c.checked ? "text-[var(--navy)]" : ""}>{c.label}</span>
          </li>
        ))}
      </ul>
      <div className="mt-2 text-[10px] text-[var(--gray-ink)]/70">
        {doneCount}/{checklist.length} itens confirmados
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Observações da onda…"
        rows={2}
        className="mt-2 w-full rounded-md border border-black/10 px-2 py-1.5 text-xs"
      />
    </div>
  );
}
